import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import {
  fetchImports,
  importsLoading,
  importsSelector,
} from 'slices/import.slice';
import { fetchOrganizationHierarchy } from 'slices/organizationHierarchy.slice';
import { useAppDispatch } from 'slices/store';
import type { TImport } from '@timeedit/types';
import {
  EImportStatus,
  EImportType,
} from '@timeedit/types/lib/enums/import.enum';
import { DefaultContentLayout, Icon } from '@timeedit/ui-components';
import { Form, Radio, Table, Tag } from '@timeedit/ui-components/antd';
import LoadingWrapper from '../../../components/LoadingWrapper';
import './index.scss';

const statusLabels: { [key: string]: { text: string; color: string } } = {
  [EImportStatus.SUCCESS]: {
    text: 'Success',
    color: 'green',
  },
  [EImportStatus.FAILURE]: {
    text: 'Failed',
    color: 'red',
  },
};

const ImportsPage = () => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();

  /**
   * SELECTORS
   */
  const imports: TImport[] = useSelector(importsSelector);
  const isLoading = useSelector(importsLoading);

  /**
   * STATE
   */
  const [importType, setImportType] = useState<string | undefined>(undefined);

  /**
   * EFFECTS
   */
  useEffect(() => {
    dispatch(fetchOrganizationHierarchy());
    setImportType(EImportType.USERS);
  }, []);

  useEffect(() => {
    if (importType) dispatch(fetchImports(importType as EImportType));
  }, [importType, dispatch]);

  const ImportTable = ({ imports }: { imports: TImport[] }) => {
    const columns = [
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: '96px',
        render: (status: string) => {
          const { text, color } = statusLabels[status];
          return (
            <Tag
              style={{ width: '64px', textAlign: 'center' }}
              color={color || 'yellow'}
            >
              {text || 'N/A'}
            </Tag>
          );
        },
      },
      {
        title: 'Time',
        dataIndex: 'timestamp',
        key: 'timestamp',
        ellipsis: true,
        width: '128px',
        render: (timestamp: number) =>
          dayjs(timestamp * 1000).format('YYYY-MM-DD HH:mm'),
      },
      {
        title: 'Message',
        dataIndex: undefined,
        key: 'message',
        ellipsis: true,
        width: '80%',
        render: (_: any, row: TImport) => {
          return row.log[row.log.length - 1].details?.toString() || 'N/A';
        },
      },
    ];

    return (
      <div className="migration-table-wrapper">
        <Table
          columns={columns}
          dataSource={imports}
          rowKey="id"
          pagination={{ size: 'small' }}
          expandable={{
            expandedRowRender: (record: TImport) => (
              <MigrationStepTable record={record} />
            ),
            rowExpandable: (record) => record.log.length > 0,

            expandIcon: ({ expanded, onExpand, record }) => (
              <RenderIcon
                expanded={expanded}
                onExpand={onExpand}
                record={record}
              />
            ),
          }}
        />
      </div>
    );
  };

  const MigrationStepTable = ({ record }: { record: TImport }) => {
    const columns = [
      {
        title: 'Step',
        dataIndex: 'step',
        key: 'step',
        width: '32px',
      },
      {
        title: 'Message',
        dataIndex: 'name',
        key: 'name',
        ellipsis: true,
      },
    ];
    return (
      <div className="migration-details-table-wrapper">
        <Table
          tableLayout="auto"
          pagination={false}
          key={record.id}
          rowKey={(log) => log.step}
          dataSource={record.log.map((log, index) => ({
            ...log,
            step: index + 1,
          }))}
          columns={columns}
          expandable={{
            expandedRowRender: (log) => <p>{log.details}</p>,
            rowExpandable: (log) => !!log.details,
            expandIcon: ({ expanded, onExpand, record }) => {
              return record.details ? (
                <RenderIcon
                  expanded={expanded}
                  onExpand={onExpand}
                  record={record}
                />
              ) : null;
            },
          }}
        />
      </div>
    );
  };

  return (
    <DefaultContentLayout title="Imports" subtitle="Imports for organization">
      <div className="inner-content--wrapper im-mt-1 allow-scroll logs-page">
        <LoadingWrapper isLoading={isLoading}>
          <Form
            layout="vertical"
            form={form}
            initialValues={{ type: EImportType.USERS }}
          >
            <Form.Item name="type" label="">
              <Radio.Group
                options={[
                  { label: 'Users', value: EImportType.USERS },
                  {
                    label: 'Organization hierarchy',
                    value: EImportType.ORGANIZATION_HIERARCHY,
                  },
                  { label: 'Auth configs', value: EImportType.AUTH_CONFIGS },
                ]}
                onChange={(e) => setImportType(e.target.value)}
                value={importType}
                optionType="button"
              />
            </Form.Item>
            {importType && <ImportTable imports={imports} />}
          </Form>
        </LoadingWrapper>
      </div>
    </DefaultContentLayout>
  );
};

export default ImportsPage;

const RenderIcon = ({ onExpand, record, expanded }: any) => {
  return (
    <button
      type="button"
      aria-label={expanded ? 'Collapse row' : 'Expand row'}
      aria-expanded={expanded}
      onClick={(e) => {
        onExpand(record, e);
        e.stopPropagation();
      }}
      style={{
        cursor: 'pointer',
        background: 'none',
        border: 'none',
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon
        id="CaretDownOutlined"
        alt="Caret Down"
        style={{
          transform: `rotate(${expanded ? '0deg' : '-90deg'})`,
          transitionDuration: '0.4s',
        }}
      />
    </button>
  );
};
